import { useContext } from 'react';
import { AuthContext } from '@/components/auth/AuthContext';
import type { AuthContextValue } from '@/types';

// ─── Extended auth value with convenience role helpers ───────────────────────

export interface UseAuthReturn extends AuthContextValue {
  /** True when the authenticated user has the 'admin' role */
  isAdmin: boolean;
  /** True when the authenticated user has the 'reviewer' role */
  isReviewer: boolean;
  /** True when the authenticated user has the 'viewer' role */
  isViewer: boolean;
  /** True when the authenticated user has the 'auditor' role */
  isAuditor: boolean;
  /** True when the authenticated user has the 'senior_reviewer' role */
  isSeniorReviewer: boolean;
  /**
   * True when the user may claim and submit reviews — admin, senior_reviewer,
   * or reviewer. Mirrors REVIEWER_ROLES in core/auth/rbac.py.
   */
  canReview: boolean;
  /**
   * True when the user may approve a job the gate flagged with
   * requires_senior_approval — i.e. senior_reviewer or admin. Mirrors
   * SENIOR_APPROVAL_ROLES in core/auth/rbac.py. This gates the UI only; the
   * server enforces the same rule in review_senior_guard.py.
   */
  canApproveSeniorLocked: boolean;
}

/**
 * Returns the auth context value with convenience role boolean getters.
 * Must be used inside <AuthProvider>.
 *
 * Role helpers:
 *   isAdmin     — admin role (Gate Rules, KB, Settings, danger zone)
 *   isReviewer  — can claim and review contracts
 *   isViewer    — read-only access to contracts and dashboard
 *   isAuditor   — read-only access plus audit trail export
 *
 * The is* flags are exact role tests — isReviewer is false for an admin. Use
 * the can* capabilities for permission checks:
 *   canReview              — may claim and submit a review (admin,
 *     senior_reviewer, reviewer). Replaces the `isAdmin || isReviewer` idiom,
 *     which would silently exclude senior_reviewer.
 *   canApproveSeniorLocked — may approve a job the gate locked to senior
 *     review (admin, senior_reviewer).
 */
const REVIEW_ROLES: readonly string[] = ['admin', 'senior_reviewer', 'reviewer'];
const SENIOR_APPROVAL_ROLES: readonly string[] = ['admin', 'senior_reviewer'];

export function useAuth(): UseAuthReturn {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return {
    ...ctx,
    isAdmin: ctx.role === 'admin',
    isReviewer: ctx.role === 'reviewer',
    isSeniorReviewer: ctx.role === 'senior_reviewer',
    isViewer: ctx.role === 'viewer',
    isAuditor: ctx.role === 'auditor',
    canReview: ctx.role !== null && REVIEW_ROLES.includes(ctx.role),
    canApproveSeniorLocked: ctx.role !== null && SENIOR_APPROVAL_ROLES.includes(ctx.role),
  };
}
